import { TFormatted } from '@ws/shared/types';
import { toast } from 'react-toastify';

import { downloadBlob } from './downloadBlob';
import { hasRenderableContent } from './hasRenderableContent';
import { sanitizeFileName } from './sanitizeFileName';

export type TDocxModule = typeof import('./docx/buildDocx');
export type TDocxModuleLoader = () => Promise<TDocxModule>;

/**
 * Точка разделения бандла: всё, что знает про OOXML, живёт за этим import()
 * и попадает в отдельный чанк, который грузится только при первом экспорте.
 */
const defaultLoader: TDocxModuleLoader = () =>
  import(/* webpackChunkName: "docx-export" */ './docx/buildDocx');

/**
 * Собирает и отдаёт .docx по скомпилированным данным коллекции.
 *
 * Оркестратор ничего не знает про формат документа, а сборщик — про интерфейс
 * и скачивание. Загрузчик инъектируется, чтобы тесты не зависели от того, во что
 * ts-jest превращает динамический import() при `module: esnext`.
 *
 * Данные принимаются функцией, а не готовым массивом: так компиляция коллекции
 * попадает внутрь той же границы ошибок. Иначе её падение обошло бы тост стороной.
 *
 * Пустая коллекция — не файл, а сообщение: отдать автору пустой документ значит
 * оставить его гадать, что пошло не так.
 *
 * Любой сбой — компиляция, загрузка чанка, сборка, упаковка, скачивание —
 * заканчивается тостом, а не молча испорченным файлом, как было с внешним конвертером.
 */
export async function exportDocx(
  getEntities: () => TFormatted[],
  nameForFile: string,
  loadDocxModule: TDocxModuleLoader = defaultLoader,
): Promise<void> {
  try {
    const entities = getEntities();

    if (!hasRenderableContent(entities)) {
      toast.error('В этой коллекции нечего экспортировать.');

      return;
    }

    const { buildDocument, packToBlob } = await loadDocxModule();
    const blob = await packToBlob(buildDocument(entities));

    downloadBlob(blob, `${sanitizeFileName(nameForFile)}.docx`);
  } catch (error) {
    console.error('Не удалось собрать docx:', error);
    toast.error('Не удалось сгенерировать docx файл. Попробуйте позже.');
  }
}
